import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Typography, Paper, Box, Button, CircularProgress, Tabs, Tab } from '@mui/material';
import ConsoleInputForm from './ConsoleInputForm';
import ConsoleResults from './ConsoleResults';
import axios from 'axios';
import CalculationHistory from '../shared/CalculationHistory';

const ConsoleCalculations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const historyRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [inputData, setInputData] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getConsoleSavePayload = useCallback(
    () => ({
      input_data: inputData || {},
      results_data:
        calculation && results ? { calculation, results } : null,
    }),
    [inputData, calculation, results],
  );

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`/api/console-calculations/saved-analyses/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const d = r.data;
        if (d.input_data) setInputData(d.input_data);
        const rd = d.results_data;
        if (rd && rd.calculation && rd.results) {
          setCalculation(rd.calculation);
          setResults(rd.results);
          setActiveTab(1);
        } else {
          setCalculation(null);
          setResults(null);
          setActiveTab(0);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAnalysis = async (data) => {
    setLoading(true);
    setError(null);
    setInputData(data);

    try {
      // Create calculation
      const createResponse = await axios.post('/api/console-calculations/calculations/', data);
      const newCalculation = createResponse.data;

      // Run analysis
      const analysisResponse = await axios.post(
        `/api/console-calculations/calculations/${newCalculation.id}/analyze/`,
        data
      );

      setCalculation(analysisResponse.data.calculation);
      setResults(analysisResponse.data.results);
      setActiveTab(1); // Switch to results tab
      historyRef.current?.saveAuto?.({
        input_data: data,
        results_data: {
          calculation: analysisResponse.data.calculation,
          results: analysisResponse.data.results,
        },
      });
    } catch (err) {
      console.error('Analysis failed:', err);
      setError(err.response?.data?.error || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (data) => {
    setInputData(data);
    // If we have results and input changed, we might want to invalidate results
    // For now, just update the input data
  };

  const resetAnalysis = () => {
    setCalculation(null);
    setResults(null);
    setError(null);
    setActiveTab(0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Concrete Console Calculations
        </Typography>
        <CalculationHistory
          ref={historyRef}
          apiEndpoint="/api/console-calculations/saved-analyses"
          getSavePayload={getConsoleSavePayload}
          appLabel="Console / corbel"
          onLoad={({ input_data, results_data }) => {
            if (input_data) setInputData(input_data);
            if (results_data?.calculation && results_data?.results) {
              setCalculation(results_data.calculation);
              setResults(results_data.results);
              setActiveTab(1);
            } else {
              setCalculation(null);
              setResults(null);
              setActiveTab(0);
            }
          }}
          renderSummary={(item) => (
            <Typography variant="caption" color="text.secondary">
              V={item.input_data?.console_vertical_load ?? '—'} kN, H=
              {item.input_data?.console_horizontal_load ?? '—'} kN
            </Typography>
          )}
        />
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Design and analyze concrete brackets/consoles supporting structural elements
      </Typography>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="console calculation tabs">
          <Tab label="Input Parameters" />
          <Tab label="Analysis Results" disabled={!results} />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <ConsoleInputForm
          onAnalyze={handleAnalysis}
          onInputChange={handleInputChange}
          initialData={inputData}
          loading={loading}
        />
      )}

      {activeTab === 1 && results && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => setActiveTab(0)}>
              Back to Input
            </Button>
            <Button variant="outlined" onClick={resetAnalysis}>
              New Analysis
            </Button>
          </Box>
          <ConsoleResults calculation={calculation} results={results} />
        </Box>
      )}
    </Container>
  );
};

export default ConsoleCalculations;
